"use client"

import {
  ClientAvatarWithNameAndTitle,
  CreativeAvatarWithNameAndTitle,
  JobTitleColorised,
  RedactedClientAvatarWithNameAndTitle,
  RedactedCreativeAvatarWithNameAndTitle,
  ShareJobModal,
} from "@/components"
import { IconButton, Image, Separator, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { formatDistanceToNow } from "date-fns"
import clsx from "clsx"
import { Square2StackIcon } from "@heroicons/react/24/outline"
import { copyToClipboard } from "@/utils/copyToClipboard"
import type { NovaSession } from "@/types"
import { useMessagingToken } from "@/hooks/useMessagingToken"
import { LockIcon, ProLockOpenIcon, ShareIcon } from "@/icons/outline"
import adjustDateForTimezone from "@/utils/dates"
import pluralize from "pluralize"
import { SkillPill } from "../../../[id]/components/SkillPill"
import { ReportJobPostingModal } from "../../ReportJobPostingModal"
import { JobDetailsContentSkeleton } from "../JobDetailsContentSkeleton"

const AuthenticatedJobDetailsContentJobPostingFragment = graphql(`
  fragment AuthenticatedJobDetailsContentJobPosting on JobPosting {
    id
    __typename
    payString
    publishedAt
    description
    title
    applicationCount
    skills {
      edges {
        node {
          id
          __typename
          name
        }
      }
    }
    coverImage {
      id
      __typename
      thumbnailUrl2000w: thumbnailUrl(width: 2000)
      thumbnailFocalPointX
      thumbnailFocalPointY
    }
    owner {
      id
      __typename
      ...CreativeAvatarWithNameAndTitle
      ...RedactedCreativeAvatarWithNameAndTitle
      ...ClientAvatarWithNameAndTitle
      ...RedactedClientAvatarWithNameAndTitle
    }
    viewerRedactionReason
    ...JobTitleColorised
  }
`)

interface Props {
  loading?: boolean
  showSkeletonImage?: boolean
  contentClassName?: string
  serverSession: NovaSession
  version?: "drawer" | "page"
  jobPosting: FragmentType<typeof AuthenticatedJobDetailsContentJobPostingFragment> | undefined | null
  openSubscriptionCheckoutModal: (jobId: string) => void
}

export const AuthenticatedJobDetailsContent: React.FC<Props> = ({
  loading = false,
  contentClassName,
  showSkeletonImage = false,
  serverSession,
  version = "drawer",
  openSubscriptionCheckoutModal,

  ...props
}) => {
  const jobPost = useFragment(AuthenticatedJobDetailsContentJobPostingFragment, props.jobPosting)
  const messagingToken = useMessagingToken()

  const isDrawerVersion = version === "drawer"

  const imageXPosition = jobPost?.coverImage?.thumbnailFocalPointX ?? 0.5
  const imageYPosition = jobPost?.coverImage?.thumbnailFocalPointY ?? 0.5

  if (loading) {
    return (
      <JobDetailsContentSkeleton
        showSkeletonImage={showSkeletonImage}
        contentClassName={contentClassName}
        isDrawerVersion={isDrawerVersion}
      />
    )
  }
  const ownerTypename = jobPost?.owner?.__typename
  const owner =
    ownerTypename &&
    ["IndividualClient", "IndividualCreative", "RedactedIndividualClient", "RedactedIndividualCreative"].includes(
      ownerTypename
    )
      ? jobPost?.owner
      : null
  const ownerIsClient = owner?.__typename === "IndividualClient"
  const ownerIsCreative = owner?.__typename === "IndividualCreative"
  const ownerIsRedactedClient = owner?.__typename === "RedactedIndividualClient"
  const ownerIsRedactedCreative = owner?.__typename === "RedactedIndividualCreative"

  return (
    <div className={clsx("relative", !isDrawerVersion && "flex flex-1 flex-col")}>
      {jobPost?.coverImage?.thumbnailUrl2000w ? (
        <div
          className={clsx(
            "mx-auto mb-6 h-[180px] w-full max-w-full px-0 md:mb-9 md:h-[260px] md:max-w-[720px]",
            !isDrawerVersion && "overflow-hidden md:rounded-xl"
          )}>
          <Image
            srcSetUrls={{
              sm: jobPost?.coverImage?.thumbnailUrl2000w,
            }}
            style={{
              objectPosition: `${imageXPosition * 100}% ${imageYPosition * 100}%`,
            }}
            alt="Job cover image"
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className={clsx(isDrawerVersion && "md:h-[80px]")} />
      )}
      <div className={clsx("mx-auto w-full", isDrawerVersion && "md:max-w-[816px] md:px-10", contentClassName)}>
        <div className={clsx("mt-6 px-5 sm:mt-0 sm:px-16 md:px-0")}>
          <div className="flex flex-row items-center justify-between">
            {/* <UserAvatarWithNameAndTitle
              user={jobPost?.createdBy}
              size={!isDrawerVersion ? "sm" : "lg"}
              variant={!isDrawerVersion ? "sm" : "default"}
              isCreative={isCreativeJobPosting}
            /> */}
            {ownerIsRedactedClient ? (
              <RedactedClientAvatarWithNameAndTitle
                size={!isDrawerVersion ? "sm" : "lg"}
                variant={!isDrawerVersion ? "sm" : "default"}
                client={owner}
                canClick={false}
                subtitleMaxLines={1}
              />
            ) : null}
            {ownerIsClient ? (
              <ClientAvatarWithNameAndTitle
                size={!isDrawerVersion ? "sm" : "lg"}
                variant={!isDrawerVersion ? "sm" : "default"}
                client={owner}
                subtitleMaxLines={1}
              />
            ) : null}
            {ownerIsRedactedCreative ? (
              <RedactedCreativeAvatarWithNameAndTitle
                size={!isDrawerVersion ? "sm" : "lg"}
                variant={!isDrawerVersion ? "sm" : "default"}
                canClick={false}
                creative={owner}
                subtitleMaxLines={1}
              />
            ) : null}
            {ownerIsCreative ? (
              <CreativeAvatarWithNameAndTitle
                size={!isDrawerVersion ? "sm" : "lg"}
                variant={!isDrawerVersion ? "sm" : "default"}
                creative={owner}
                subtitleMaxLines={1}
              />
            ) : null}
            <div className="flex flex-row gap-x-3">
              {serverSession.account === "client" ? (
                <IconButton
                  variant={isDrawerVersion ? "white" : "grey"}
                  Icon={Square2StackIcon}
                  onClick={() => copyToClipboard(window.location.href)}
                />
              ) : (
                <>
                  {messagingToken && (
                    <ShareJobModal
                      jobId={jobPost?.id}
                      Trigger={<IconButton variant={isDrawerVersion ? "white" : "grey"} Icon={ShareIcon} />}
                      userUUID={serverSession.user?.uuid}
                      messagingToken={messagingToken}
                    />
                  )}
                </>
              )}
              <ReportJobPostingModal jobPostingId={jobPost?.id} buttonVariant={isDrawerVersion ? "white" : "grey"} />
            </div>
          </div>
          <div className="mt-6">
            <JobTitleColorised commonWordColor="text-fg-2" textVariant="h5" jobPost={jobPost} />
          </div>
          {jobPost?.skills?.edges && jobPost.skills.edges.length > 0 ? (
            <div className="mt-6 flex flex-row flex-wrap gap-1">
              {jobPost?.skills?.edges?.map(edge => {
                return <SkillPill key={edge?.node?.id} skill={edge?.node?.name} />
              })}
            </div>
          ) : null}
          <div className="mt-6">
            <div className="flex w-fit flex-row items-center gap-x-2 rounded-full">
              <Text variant="body2" className="text-fg-2">
                Posted{" "}
                {formatDistanceToNow(jobPost?.publishedAt || adjustDateForTimezone(new Date().toISOString())!, {
                  addSuffix: true,
                })}
                {" - "}
                {jobPost?.viewerRedactionReason !== null ? (
                  <># of applications:</>
                ) : (
                  <>
                    {jobPost?.applicationCount || 0} {pluralize("application", jobPost?.applicationCount || 0)}
                  </>
                )}
              </Text>
              {jobPost?.viewerRedactionReason !== null ? <LockIcon className="size-4 stroke-fg-2" /> : null}
            </div>
          </div>
          <div className="mt-6 w-full">
            <Separator className="bg-bg-3" />
          </div>
          <div className="mt-4 flex flex-row gap-x-2 sm:mt-6">
            <Text className="text-fg-2">Title</Text>
          </div>
          <Text className="mt-2 flex flex-col gap-y-4 whitespace-pre-wrap">{jobPost?.title}</Text>
          <div className="mt-4 flex flex-row gap-x-2 sm:mt-6">
            <Text className="text-fg-2">Description</Text>
          </div>
          {jobPost?.viewerRedactionReason === null ? (
            <Text className="mb-10 mt-2 flex flex-col gap-y-4 whitespace-pre-wrap">
              {jobPost?.description || "Job has no description"}
            </Text>
          ) : (
            <div
              style={{
                background:
                  "linear-gradient(180deg, rgba(212, 212, 212, 0.70) 11.54%, rgba(179, 179, 179, 0.00) 93.06%)",
              }}
              onClick={() => openSubscriptionCheckoutModal(jobPost?.id!)}
              className="mt-3 flex h-[256px] w-full cursor-pointer select-none flex-row items-center justify-center gap-x-1 rounded-lg">
              <Text variant="h6" className="text-fg-1">
                Unlock with{" "}
                <Text className="text-pro-gradient-dark" variant="h6" element="span">
                  Pro
                </Text>
              </Text>

              <ProLockOpenIcon className="size-6" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
